import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  ViewChild,
  inject,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@src/app/core/services/helpers/permissions.service';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '@src/app/shared/shared.module';
import { CommitteeHttpService } from '@src/app/core/services/http/monitoring/committee-http.service';
import { RiskMonitoringHttpService } from '@src/app/core/services/http/risk-monitoring/risk-monitoring-http.service';
import { LoanOperationHttpService } from '@src/app/core/services/http/loanOperation/loan-operation-http.service';
import { PipelinesHttpService } from '@src/app/core/services/http/pipelines/pipelines-http.service';
import { DerivativesHttpService } from '@src/app/core/services/http/derivatives/derivatives-http.service';
import { ToastHelperService } from '@src/app/core/services/helpers/toast-helper.service';
import { FormErrorHandlerService } from '@src/app/core/services/helpers/form-error-handler.service';
import { DateFormatterService } from '@src/app/core/services/helpers/date-formatter.service';
import { RiskCommittee } from '@interfaces/control/monitoring/responses/risk-committee.response';
import { CreateRiskCommitteeRequest } from '@interfaces/control/monitoring/requests/create-risk-committee.request';
import { CommitteeApprovalType } from '@interfaces/control/monitoring/responses/risk-committee.response';
import { Option } from '@interfaces/common/option';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { RfqResponse } from '@interfaces/risk-monitoring/responses/rfq.response';
import { LoanOperationResponse } from '@interfaces/loan/loan-operation.response';
import { SelectLoanComponent } from '@src/app/shared/components/select-loan/select-loan.component';
import { buildLoanOptionSearchLabel } from '@src/app/shared/utils/loan-operation-select-display.util';
import { DerivativeOptionData } from '@src/app/core/services/http/derivatives/derivatives-http.service';
import {
  CommitteeDerivativeRef,
  buildDerivativeChoices,
  formatDerivativeRefs,
  buildCommitteeDerivativesDescription,
  loadLinkedInstallmentIds,
  normalizePrefillDerivatives,
  sameIdSet,
} from './committee-derivatives.util';
import { forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

/** Compara GUIDs sem diferenciar caixa (o Painel manda maiúsculo em alguns caminhos). */
function sameId(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}
import { MonitoringUserPicklistComponent } from '@features/dashboard/control/risk-management/monitoring/components/monitoring-user-picklist/monitoring-user-picklist.component';
import { MonitoringPicklistMember } from '@features/dashboard/control/risk-management/monitoring/components/monitoring-user-picklist/monitoring-user-picklist.model';

export interface CommitteePrefillOptions {
  fromDerivativeList?: boolean;
  companyId?: string;
  companyName?: string;
  pipelineCardId?: string;
  pipelineCardRef?: string;
  loanOperationId?: string;
  loanOperationContractId?: string;
  loanOperationInstallmentId?: string;
  loanOperationInstallmentNumber?: number | null;
  riskMandateId?: string;
  mandateSequentialId?: number;
  rfqId?: string;
  rfqSequentialId?: number | null;
  derivativeOperationId?: string;
  derivativeSequentialId?: string;
  /**
   * Vários derivativos num comitê só (card 3108, seleção da tela de Derivativos). Tem prioridade
   * sobre `derivativeOperationId`; as parcelas pré-marcadas são a união das parcelas de todos.
   */
  derivatives?: CommitteeDerivativeRef[];
  /**
   * Derivativos que o usuário pode marcar ou desmarcar no campo Derivativos (os salvos da demanda,
   * vindo da tela de Derivativos). Com esta lista o campo vira multisseleção em vez de travado.
   */
  availableDerivatives?: CommitteeDerivativeRef[];
  /**
   * Várias parcelas pré-selecionadas de uma vez (Painel de Risco: uma linha simulada por
   * parcela). Ignorado quando há `derivativeOperationId` — nesse caso a modal resolve as
   * parcelas do próprio derivativo no backend.
   */
  loanOperationInstallments?: { id: string; number: number | null }[];
  /** Texto inicial do campo "Descrição / Contexto" (ex.: resumo da simulação do painel). */
  description?: string;
}

@Component({
  selector: 'app-modal-create-committee',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    MonitoringUserPicklistComponent,
    SelectLoanComponent,
  ],
  templateUrl: './modal-create-committee.component.html',
  styleUrls: ['./modal-create-committee.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalCreateCommitteeComponent {
  @ViewChild('dialog') dialogRef!: DialogComponent;
  @Output() committeeSaved = new EventEmitter<RiskCommittee>();
  readonly viewMandateDetailsRequested = output<string>();

  readonly today = new Date(new Date().setHours(0, 0, 0, 0));

  currentStep = 1;
  isSubmitting = false;

  // Dados do formulário
  title = '';
  description = '';
  /** Descrição digitada (ou vinda da simulação do Painel): a automática dos derivativos não toca. */
  descriptionManuallyEdited = false;
  companyId = '';
  companyName = '';
  approvalType: CommitteeApprovalType = 'Majority60Percent';
  deadline: string | null = null;
  deadlineTime = '08:00';
  members: MonitoringPicklistMember[] = [];
  memberPicklistResetVersion = 0;

  // Vínculos — IDs selecionados
  rfqId = '';
  rfqSequentialId: number | null = null;
  loanId = '';
  loanContractId = '';
  installmentIds: string[] = [];
  installmentNumbers: number[] = [];
  pipelineCardId = '';
  pipelineCardRef = '';

  // Vínculos — Derivativos. `derivativeId` é o select manual (um só); `linkedDerivatives` é o
  // que vai para a API, com um ou vários (prefill da tela de Derivativos).
  derivativeId = '';
  derivativeSequentialId = '';
  linkedDerivatives: CommitteeDerivativeRef[] = [];
  /**
   * Ids dos derivativos marcados, para o `ngModel` da multisseleção. Campo (não getter) de propósito:
   * um array novo a cada ciclo faz o `app-select` reemitir o valor e a modal pedir as parcelas em laço.
   */
  linkedDerivativeIds: string[] = [];
  /** Derivativos da demanda para a multisseleção (vazio = campo travado ou select manual). */
  availableDerivatives: CommitteeDerivativeRef[] = [];
  availableDerivativeOptions: Option[] = [];
  private derivativeInstallmentsRequest = 0;

  // Opções para os selects de vínculo
  rfqOptions: Option[] = [];
  loanOptions: Option[] = [];
  installmentOptions: Option[] = [];
  pipelineOptions: Option[] = [];
  derivativeOptions: Option[] = [];
  /**
   * Derivativos da empresa filtrados pelo empréstimo escolhido (card 3278). Campo, não getter: um
   * array novo a cada ciclo faz o `app-select` reemitir e entrar em laço (ver README, card 3108).
   */
  visibleDerivativeOptions: Option[] = [];

  // Flags de loading
  isLoadingRfqs = false;
  isLoadingLoans = false;
  isLoadingPipelines = false;
  isLoadingDerivatives = false;

  // Controle de título editado manualmente
  titleManuallyEdited = false;

  // Campos bloqueados (pré-preenchidos via prefill do kanban)
  prefillLocked = false;
  loanPrefilled = false;
  installmentPrefilled = false;
  mandatePrefilled = false;
  riskMandateId: string | null = null;
  mandateSequentialId: number | null = null;
  rfqPrefilled = false;
  derivativePrefilled = false;
  derivativeListMode = false;

  // Cache de entidades carregadas para cruzamento de dados
  private _rfqList: RfqResponse[] = [];
  private _loanList: LoanOperationResponse[] = [];
  private _derivativeInstallmentIds: string[] = [];
  private shouldApplyDerivativeInstallmentsAfterLoanLoad = false;
  /** Empréstimo dos derivativos vindos da tela de Derivativos; trocar de empréstimo desfaz o vínculo. */
  private derivativeListLoanId = '';

  isLoadingDerivativeDetails = false;

  /**
   * A modal já foi aberta. Ela fica montada nas telas de Derivativos, Risco e Monitoramento, e o seletor de empresa
   * emite a empresa padrão ao montar; sem este marcador, toda abertura dessas telas buscava RFQs, empréstimos,
   * pipelines e derivativos à toa, e quem não tem acesso a pipelines via 403 (card 3902). A busca acontece ao abrir.
   */
  private hasBeenOpened = false;

  /** Pipelines são do módulo de Negociação: só busca o vínculo com pipeline quem pode listá-los (card 3902). */
  private readonly canListPipelines = toSignal(
    inject(PermissionsService).hasPermission(Permissions.Pipeline.LIST),
    { initialValue: false }
  );

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly committeeHttpService: CommitteeHttpService,
    private readonly riskMonitoringHttpService: RiskMonitoringHttpService,
    private readonly loanOperationHttpService: LoanOperationHttpService,
    private readonly pipelinesHttpService: PipelinesHttpService,
    private readonly derivativesHttpService: DerivativesHttpService,
    private readonly toastHelperService: ToastHelperService,
    private readonly formErrorHandlerService: FormErrorHandlerService,
    private readonly dateFormatterService: DateFormatterService
  ) {}

  readonly dialogKey = 'committee-create';

  open(prefill?: CommitteePrefillOptions): void {
    this.hasBeenOpened = true;
    this.reset();
    this.derivativeListMode = !!prefill?.fromDerivativeList;
    const prefillDerivatives = prefill ? normalizePrefillDerivatives(prefill) : [];
    const hasDerivativePrefill = prefillDerivatives.length > 0;
    if (prefill?.companyId) {
      this.companyId = prefill.companyId;
      this.companyName = prefill.companyName ?? '';
      this.prefillLocked = true;
    }
    if (prefill?.pipelineCardId) {
      this.pipelineCardId = prefill.pipelineCardId;
      this.pipelineCardRef = prefill.pipelineCardRef ?? '';
      this.pipelineOptions = [{ label: this.pipelineCardRef, value: this.pipelineCardId }];
    }
    if (prefill?.loanOperationId) {
      this.loanId = prefill.loanOperationId;
      this.loanContractId = prefill.loanOperationContractId ?? '';
      this.loanOptions = [{ label: this.loanContractId || this.loanId, value: this.loanId }];
      this.loanPrefilled = true;
      if (this.derivativeListMode) this.derivativeListLoanId = this.loanId;
    }
    if (prefill?.loanOperationInstallments?.length && !hasDerivativePrefill) {
      // Lote de parcelas (Painel de Risco): pré-seleciona todas e deixa o título automático
      // se montar a partir delas, como no prefill de parcela única.
      this.installmentIds = prefill.loanOperationInstallments.map(item => item.id);
      this.installmentNumbers = prefill.loanOperationInstallments
        .map(item => item.number)
        .filter((number): number is number => number != null);
      this.installmentOptions = prefill.loanOperationInstallments.map(item => ({
        label: `Parcela #${item.number ?? ''}`,
        value: item.id,
      }));
      this.installmentPrefilled = !this.derivativeListMode;
      if (!this.titleManuallyEdited) {
        this.generateAutoTitle();
      }
    } else if (prefill?.loanOperationInstallmentId && !hasDerivativePrefill) {
      this.installmentIds = [prefill.loanOperationInstallmentId];
      this.installmentNumbers =
        prefill.loanOperationInstallmentNumber != null
          ? [prefill.loanOperationInstallmentNumber]
          : [];
      this.installmentOptions = [
        {
          label: `Parcela #${prefill.loanOperationInstallmentNumber ?? ''}`,
          value: prefill.loanOperationInstallmentId,
        },
      ];
      this.installmentPrefilled = !this.derivativeListMode;
    }
    if (prefill?.description && !this.description) {
      this.description = prefill.description;
      // A descrição da simulação do Painel fica; a da tela de Derivativos acompanha a seleção.
      this.descriptionManuallyEdited = !this.derivativeListMode;
    }
    if (prefill?.riskMandateId) {
      this.riskMandateId = prefill.riskMandateId;
      this.mandateSequentialId = prefill.mandateSequentialId ?? null;
      this.mandatePrefilled = true;
      if (!this.titleManuallyEdited) {
        this.generateAutoTitle();
      }
    }
    if (prefill?.rfqId) {
      this.rfqId = prefill.rfqId;
      this.rfqSequentialId = prefill.rfqSequentialId ?? null;
      this.rfqOptions = [{ label: `#${this.rfqSequentialId ?? ''} — RFQ`, value: this.rfqId }];
      this.rfqPrefilled = true;
      if (!this.titleManuallyEdited) {
        this.generateAutoTitle();
      }
    }
    if (hasDerivativePrefill) {
      this.setLinkedDerivatives(prefillDerivatives);
      this.derivativeId = prefillDerivatives[0].id;
      this.derivativeSequentialId = prefillDerivatives[0].sequentialId;
      this.derivativeOptions = prefillDerivatives.map(item => ({
        label: `#${item.sequentialId}`,
        value: item.id,
      }));
      this.derivativePrefilled = true;
      this.setAvailableDerivatives(prefill?.availableDerivatives ?? [], prefillDerivatives);
      this.resolveDerivativeInstallments(prefillDerivatives.map(item => item.id));
    }
    // Carregar vínculos disponíveis sempre que houver empresa pré-preenchida
    if (this.companyId) {
      this.loadRfqsByCompany();
      this.loadLoansByCompany();
      this.loadPipelinesByCompany();
      this.loadDerivativesByCompany();
    }
    this.dialogRef?.open('', '', '', '', '', this.dialogKey, false, false);
    this.cdr.markForCheck();
  }

  /** Rótulo dos derivativos vinculados (`DERIV3818, DERIV3819`), no campo travado, na revisão e no título. */
  get derivativesLabel(): string {
    return formatDerivativeRefs(this.linkedDerivatives);
  }

  get lockNonInstallmentLinks(): boolean {
    return this.derivativeListMode;
  }

  get canUseMajority(): boolean {
    return this.voterCount > 2;
  }

  get stepValid(): boolean {
    switch (this.currentStep) {
      case 1:
        return !!this.title.trim() && !!this.companyId;
      case 2:
        return this.members.length >= 2;
      case 3:
        return !!this.deadline && this.isDeadlineFuture;
      case 4:
        return this.hasAtLeastOneVoter;
      default:
        return false;
    }
  }

  get isDeadlineFuture(): boolean {
    if (!this.deadline) return false;
    return new Date(`${this.deadline}T${this.deadlineTime || '00:00'}`) > new Date();
  }

  get hasAtLeastOneVoter(): boolean {
    return this.members.some(m => m.hasVotingPower);
  }

  get voterCount(): number {
    return this.members.filter(m => m.hasVotingPower).length;
  }

  get approvalThreshold(): number {
    if (this.approvalType === 'Majority60Percent') {
      return Math.ceil(this.voterCount * 0.6);
    }
    return this.voterCount;
  }

  get hasInstallments(): boolean {
    return this.installmentOptions.length > 0;
  }

  nextStep(): void {
    if (!this.stepValid) return;
    if (this.currentStep === 2 && !this.canUseMajority) {
      this.approvalType = 'Unanimous';
    }
    if (this.currentStep < 4) {
      this.currentStep++;
      this.cdr.markForCheck();
    }
  }

  onCompanySelect(option: Option | null): void {
    this.companyId = option?.value ?? '';
    this.companyName = option?.label ?? '';

    // Limpar vínculos ao trocar empresa
    this._clearLinkSelections();

    if (this.companyId && this.hasBeenOpened) {
      this.loadRfqsByCompany();
      this.loadLoansByCompany();
      this.loadPipelinesByCompany();
      this.loadDerivativesByCompany();
    }

    this.cdr.markForCheck();
  }

  onRfqSelect(option: Option | null): void {
    this.rfqId = option?.value ?? '';
    this.rfqSequentialId = option ? Number(option.value) : null;

    // Resgatar o sequentialId real do cache
    const rfq = this._rfqList.find(r => r.id === this.rfqId);
    this.rfqSequentialId = rfq?.sequentialId ?? null;

    // Auto-preencher empréstimo se RFQ tiver vínculo
    if (rfq?.loanOperationId) {
      const loan = this._loanList.find(l => l.id === rfq.loanOperationId);
      if (loan) {
        this.loanId = loan.id ?? '';
        this.loanContractId = loan.sequentialId ?? loan.contractId ?? '';
        this._populateInstallments(loan);
      }
    } else if (!option) {
      // Limpou RFQ — não forçar limpeza do loan se usuário já selecionou manualmente
    }

    if (!this.titleManuallyEdited) {
      this.generateAutoTitle();
    }
    this.cdr.markForCheck();
  }

  /** Seletor padrão de LOAN (`app-select-loan`, card 3278): devolve só o id. */
  onLoanValueChange(value: string | string[] | null): void {
    const id = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
    if (id === this.loanId) return;
    this.onLoanSelect(this.loanOptions.find(option => option.value === id) ?? null);
  }

  onLoanSelect(option: Option | null): void {
    this.loanId = option?.value ?? '';
    const loan = this._loanList.find(l => sameId(l.id, this.loanId));
    // Código LOAN no título, como no prefill (card 3107); contrato do banco só sem o código.
    this.loanContractId = loan?.sequentialId ?? loan?.contractId ?? '';

    // Card 3278: o empréstimo vem selecionado mas é editável. Trocar para outro LOAN desfaz os
    // derivativos que vieram da tela de Derivativos (são de outra operação) e volta o campo
    // Derivativo para a lista da empresa, filtrada pelo novo empréstimo.
    if (this.derivativeListLoanId && !sameId(this.loanId, this.derivativeListLoanId)) {
      this.derivativeListMode = false;
      this.derivativeListLoanId = '';
      this.derivativePrefilled = false;
      this.availableDerivatives = [];
      this.availableDerivativeOptions = [];
      this.clearSelectedDerivative();
    } else if (
      this.linkedDerivativeIds.some(id => !this.derivativeBelongsToLoan(id, this.loanId))
    ) {
      this.clearSelectedDerivative();
    }
    this.refreshVisibleDerivativeOptions();

    // Resetar parcelas ao trocar empréstimo. A parcela travada do Painel era do LOAN anterior:
    // com outro empréstimo o campo Parcela(s) volta a ser escolha livre.
    this.installmentPrefilled = false;
    this.installmentIds = [];
    this.installmentNumbers = [];

    if (loan) {
      if (this._derivativeInstallmentIds.length > 0) {
        this._applyDerivativeInstallmentFilter();
      } else {
        this._populateInstallments(loan);
      }
    } else {
      this.installmentOptions = [];
    }

    if (!this.titleManuallyEdited) {
      this.generateAutoTitle();
    }
    this.cdr.markForCheck();
  }

  onInstallmentSelect(options: Option[] | null): void {
    const selected = options ?? [];
    this.installmentIds = selected.map(o => o.value);

    const loan = this._loanList.find(l => sameId(l.id, this.loanId));
    this.installmentNumbers = selected
      .map(o => loan?.flowParams?.installments?.find(i => i.id === o.value)?.installmentNumber)
      .filter((n): n is number => n != null);

    if (!this.titleManuallyEdited) {
      this.generateAutoTitle();
    }
    this.cdr.markForCheck();
  }

  onPipelineCardSelect(option: Option | null): void {
    this.pipelineCardId = option?.value ?? '';
    this.pipelineCardRef = option?.label ?? '';

    if (!this.titleManuallyEdited) {
      this.generateAutoTitle();
    }
    this.cdr.markForCheck();
  }

  /** Tira o derivativo escolhido (e as parcelas que vinham dele), sem mexer no empréstimo. */
  private clearSelectedDerivative(): void {
    this.derivativeId = '';
    this.derivativeSequentialId = '';
    this.setLinkedDerivatives([]);
    this._derivativeInstallmentIds = [];
    this.shouldApplyDerivativeInstallmentsAfterLoanLoad = false;
    this.refreshAutoDescription();
  }

  /** Derivativo de outro LOAN sai ao trocar o empréstimo; sem o dado (opção ainda não carregada), fica. */
  private derivativeBelongsToLoan(derivativeId: string, loanId: string): boolean {
    if (!loanId) return true;
    const option = this.derivativeOptions.find(item => item.value === derivativeId);
    if (!option) return true;
    return sameId((option.data as DerivativeOptionData | undefined)?.loanOperationId, loanId);
  }

  /**
   * Opções do campo Derivativo: com empréstimo escolhido, só os derivativos daquele LOAN (pedido do
   * Tiago no teste do card 3278: derivativo sem empréstimo na lista atrapalhava a escolha). Os já
   * marcados continuam na lista para não sumirem do campo.
   */
  private refreshVisibleDerivativeOptions(): void {
    const linked = new Set(this.linkedDerivativeIds.map(id => id.toLowerCase()));
    this.visibleDerivativeOptions = this.loanId
      ? this.derivativeOptions.filter(
          option =>
            sameId(
              (option.data as DerivativeOptionData | undefined)?.loanOperationId,
              this.loanId
            ) || linked.has(String(option.value).toLowerCase())
        )
      : this.derivativeOptions;
  }

  private setLinkedDerivatives(derivatives: CommitteeDerivativeRef[]): void {
    this.linkedDerivatives = derivatives;
    this.linkedDerivativeIds = derivatives.map(item => item.id);
  }

  private setAvailableDerivatives(
    available: CommitteeDerivativeRef[],
    linked: CommitteeDerivativeRef[]
  ): void {
    const { refs, options } = buildDerivativeChoices(available, linked);
    this.availableDerivatives = refs;
    this.availableDerivativeOptions = options;
  }

  /**
   * Marcou ou desmarcou derivativo na multisseleção: atualiza o vínculo e refaz as parcelas
   * pré-marcadas (união das parcelas dos que ficaram). Sem nenhum, volta a lista completa de
   * parcelas do empréstimo.
   */
  /**
   * Multisseleção de derivativos da empresa (card 3278), no Novo Comitê aberto direto ou pelo
   * Painel: mesmo comportamento da tela de Derivativos (card 3108), com as opções do campo.
   */
  onCompanyDerivativesChange(ids: string[] | null): void {
    if (sameIdSet(ids ?? [], this.linkedDerivativeIds)) return;
    const selected = new Set((ids ?? []).map(id => id.toLowerCase()));
    this.setLinkedDerivatives(
      this.derivativeOptions
        .filter(option => selected.has(String(option.value).toLowerCase()))
        .map(option => ({
          id: option.value,
          sequentialId:
            (option.data as DerivativeOptionData | undefined)?.sequentialId ?? option.label,
        }))
    );
    this.derivativeId = this.linkedDerivatives[0]?.id ?? '';
    this.derivativeSequentialId = this.linkedDerivatives[0]?.sequentialId ?? '';
    this.refreshAutoDescription();

    if (this.linkedDerivatives.length === 0) {
      this.derivativeInstallmentsRequest++; // descarta pedido de parcelas ainda em voo
      this.isLoadingDerivativeDetails = false;
      this._derivativeInstallmentIds = [];
      this.shouldApplyDerivativeInstallmentsAfterLoanLoad = false;
      const loan = this._loanList.find(l => sameId(l.id, this.loanId));
      if (loan) this._populateInstallments(loan);
      if (!this.titleManuallyEdited) this.generateAutoTitle();
      this.cdr.markForCheck();
      return;
    }

    this.resolveDerivativeInstallments(this.linkedDerivativeIds);
  }

  onLinkedDerivativesChange(ids: string[] | null): void {
    if (sameIdSet(ids ?? [], this.linkedDerivativeIds)) return;
    const selected = new Set((ids ?? []).map(id => id.toLowerCase()));
    this.setLinkedDerivatives(
      this.availableDerivatives.filter(item => selected.has(item.id.toLowerCase()))
    );
    this.derivativeId = this.linkedDerivatives[0]?.id ?? '';
    this.derivativeSequentialId = this.linkedDerivatives[0]?.sequentialId ?? '';
    this.refreshAutoDescription();

    if (this.linkedDerivatives.length === 0) {
      this.derivativeInstallmentsRequest++; // descarta pedido de parcelas ainda em voo
      this.isLoadingDerivativeDetails = false;
      this._derivativeInstallmentIds = [];
      this.shouldApplyDerivativeInstallmentsAfterLoanLoad = false;
      const loan = this._loanList.find(l => sameId(l.id, this.loanId));
      if (loan) this._populateInstallments(loan);
      if (!this.titleManuallyEdited) this.generateAutoTitle();
      this.cdr.markForCheck();
      return;
    }

    this.resolveDerivativeInstallments(this.linkedDerivativeIds);
  }

  /**
   * Parcelas pré-marcadas = união das parcelas vinculadas a cada derivativo (card 3108). Com um
   * derivativo só, é o mesmo comportamento de antes.
   */
  private resolveDerivativeInstallments(derivativeIds: string[]): void {
    const ids = derivativeIds.filter(Boolean);
    if (ids.length === 0) return;

    this.isLoadingDerivativeDetails = true;
    this.cdr.markForCheck();
    // Só a resposta do último pedido vale: marcar e desmarcar rápido não deixa parcela velha.
    const request = ++this.derivativeInstallmentsRequest;

    loadLinkedInstallmentIds(this.derivativesHttpService, ids).subscribe({
      next: installmentIds => {
        if (request !== this.derivativeInstallmentsRequest) return;
        this.isLoadingDerivativeDetails = false;
        this._derivativeInstallmentIds = installmentIds;
        this.shouldApplyDerivativeInstallmentsAfterLoanLoad =
          this._derivativeInstallmentIds.length > 0;

        if (this._derivativeInstallmentIds.length > 0 && this._loanList.length > 0) {
          this._applyDerivativeInstallmentFilter();
        }

        this.cdr.markForCheck();
      },
      error: () => {
        if (request !== this.derivativeInstallmentsRequest) return;
        this._derivativeInstallmentIds = [];
        this.shouldApplyDerivativeInstallmentsAfterLoanLoad = false;
        this.isLoadingDerivativeDetails = false;
        this.cdr.markForCheck();
      },
    });
  }

  private _applyDerivativeInstallmentFilter(): void {
    const ids = this._derivativeInstallmentIds;
    if (ids.length === 0) return;
    const normalizedIds = new Set(ids.map(id => id.toLowerCase()));

    // Encontra o empréstimo que contém essas parcelas
    const matchingLoan = this._loanList.find(l =>
      l.flowParams?.installments?.some(i => normalizedIds.has(i.id.toLowerCase()))
    );

    if (matchingLoan) {
      // Auto-seleciona o empréstimo
      this.loanId = matchingLoan.id ?? '';
      this.loanContractId = matchingLoan.sequentialId ?? matchingLoan.contractId ?? '';

      // Filtra opções de parcela apenas para as vinculadas ao derivativo
      const filtered = (matchingLoan.flowParams?.installments ?? []).filter(i =>
        normalizedIds.has(i.id.toLowerCase())
      );

      this.applyAllDerivativeInstallmentsSelection(filtered);
      this.shouldApplyDerivativeInstallmentsAfterLoanLoad = false;
      if (!this.titleManuallyEdited) {
        this.generateAutoTitle();
      }
      return;
    }

    // Fallback: quando não encontra empréstimo por conta de sincronização de estado,
    // usa a interseção dos IDs vinculados com as opções atuais de parcela.
    if (this.installmentOptions.length > 0) {
      const fallbackOptions = this.installmentOptions.filter(option =>
        normalizedIds.has(String(option.value).toLowerCase())
      );

      if (fallbackOptions.length > 0) {
        this.installmentIds = fallbackOptions.map(option => option.value);
        const selectedLoan = this._loanList.find(loan => loan.id === this.loanId);
        this.installmentNumbers = fallbackOptions
          .map(
            option =>
              selectedLoan?.flowParams?.installments?.find(i => i.id === option.value)
                ?.installmentNumber
          )
          .filter((n): n is number => n != null);
        this.shouldApplyDerivativeInstallmentsAfterLoanLoad = false;
      }
    }
  }

  private applyAllDerivativeInstallmentsSelection(
    installments: Array<{ id: string; installmentNumber: number; dueAt: string }> | null | undefined
  ): void {
    const safeInstallments = installments ?? [];
    this.installmentOptions = safeInstallments.map(i => ({
      label: `Parcela #${i.installmentNumber} — ${this.dateFormatterService.formatDateInFormat(new Date(i.dueAt), 'dd/MM/yyyy')}`,
      value: i.id,
    }));
    this.installmentIds = safeInstallments.map(i => i.id);
    this.installmentNumbers = safeInstallments.map(i => i.installmentNumber);
  }

  onTitleInput(): void {
    this.titleManuallyEdited = true;
  }

  /** Texto digitado pelo usuário: a descrição automática dos derivativos não sobrescreve mais. */
  onDescriptionInput(): void {
    this.descriptionManuallyEdited = true;
  }

  /**
   * Descrição automática dos derivativos marcados (card 3278), no formato do Painel de Risco, com os
   * números que já vieram nas opções do campo Derivativo. Não mexe em texto editado à mão nem na
   * descrição da simulação do Painel (que não é de derivativo cadastrado).
   */
  private refreshAutoDescription(): void {
    if (this.descriptionManuallyEdited) return;
    const rows = this.linkedDerivatives.map(ref => {
      const data = this.derivativeOptions.find(option => option.value === ref.id)?.data as
        | DerivativeOptionData
        | undefined;
      return { ...data, sequentialId: data?.sequentialId ?? ref.sequentialId };
    });
    const source = this.derivativeListMode
      ? 'Derivativos da tela de Derivativos'
      : 'Derivativos selecionados';
    this.description = buildCommitteeDerivativesDescription(
      this.loanContractId ? `${source} — ${this.loanContractId}` : source,
      rows
    );
  }

  generateAutoTitle(): void {
    const parts: string[] = [];

    if (this.mandatePrefilled && this.mandateSequentialId != null) {
      parts.push(`MGR #${this.mandateSequentialId}`);
    }

    if (this.rfqSequentialId != null) {
      parts.push(`RFQ #${this.rfqSequentialId}`);
    }

    if (this.loanContractId) {
      if (this.installmentNumbers.length > 0) {
        const nums = this.installmentNumbers.map(n => `#${n}`).join(', ');
        parts.push(`Parcela ${nums} / Empréstimo ${this.loanContractId}`);
      } else {
        parts.push(`Empréstimo ${this.loanContractId}`);
      }
    }

    if (this.linkedDerivatives.length > 1) {
      parts.push(`Derivativos ${this.derivativesLabel}`);
    }

    this.title = parts.length > 0 ? `Comitê - ${parts.join(' / ')}` : 'Comitê';
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.cdr.markForCheck();
    }
  }

  goToStep(step: number): void {
    if (step < this.currentStep) {
      this.currentStep = step;
      this.cdr.markForCheck();
    }
  }

  submit(): void {
    if (!this.hasAtLeastOneVoter || !this.deadline) return;

    this.isSubmitting = true;
    this.cdr.markForCheck();

    const payload: CreateRiskCommitteeRequest = {
      companyId: this.companyId || undefined,
      riskMandateId: this.riskMandateId || undefined,
      rfqId: this.rfqId || undefined,
      rfqSequentialId: this.rfqSequentialId ?? undefined,
      loanOperationId: this.loanId || undefined,
      loanOperationContractId: this.loanContractId || undefined,
      loanOperationInstallmentId: this.installmentIds[0] || undefined,
      loanOperationInstallmentNumber: this.installmentNumbers[0] ?? undefined,
      loanOperationInstallmentIds: this.installmentIds.length > 0 ? this.installmentIds : undefined,
      loanOperationInstallmentNumbers:
        this.installmentNumbers.length > 0 ? this.installmentNumbers : undefined,
      pipelineCardId: this.pipelineCardId || undefined,
      pipelineCardReference: this.pipelineCardRef || undefined,
      derivativeOperationId: this.linkedDerivatives[0]?.id,
      derivativeOperationIds:
        this.linkedDerivatives.length > 0 ? this.linkedDerivatives.map(item => item.id) : undefined,
      title: this.title.trim(),
      description: this.description.trim(),
      approvalType: this.approvalType,
      deadline: new Date(`${this.deadline}T${this.deadlineTime}`).toISOString(),
      members: this.members.map(m => ({
        memberType: m.memberType,
        userId: m.userId,
        name: m.name,
        roleLabel: m.roleLabel,
        hasVotingPower: m.hasVotingPower,
      })),
    };

    this.committeeHttpService.create(payload).subscribe({
      next: response => {
        this.isSubmitting = false;
        if (response) {
          this.toastHelperService.showToastSuccess('Comitê criado com sucesso!');
          this.committeeSaved.emit(response);
          this.dialogRef?.close();
        }
        this.cdr.markForCheck();
      },
      error: error => {
        this.isSubmitting = false;
        this.formErrorHandlerService.showToastErrors(error?.error ?? error);
        this.cdr.markForCheck();
      },
    });
  }

  formatDeadlineLabel(): string {
    if (!this.deadline) return '-';
    const date = new Date(`${this.deadline}T${this.deadlineTime || '00:00'}`);
    return (
      this.dateFormatterService.formatDateInFormat(date, 'dd/MM/yyyy') + ` ${this.deadlineTime}`
    );
  }

  private loadRfqsByCompany(): void {
    if (!this.companyId) return;
    this.isLoadingRfqs = true;
    this.cdr.markForCheck();

    this.riskMonitoringHttpService.getRfqs({ companyId: this.companyId }).subscribe({
      next: rfqs => {
        this._rfqList = rfqs ?? [];
        this.rfqOptions = this._rfqList.map(r => ({
          label: `#${r.sequentialId} — ${r.subcategory}`,
          value: r.id,
        }));

        if (this.rfqPrefilled && this.rfqId && !this.loanPrefilled && !this.loanId) {
          const linkedRfq = this._rfqList.find(r => r.id === this.rfqId);
          if (linkedRfq?.loanOperationId) {
            this.loanId = linkedRfq.loanOperationId;
            const loan = this._loanList.find(l => sameId(l.id, this.loanId));
            if (loan) {
              this.loanContractId = loan.sequentialId ?? loan.contractId ?? '';
              this._populateInstallments(loan);
              if (!this.titleManuallyEdited) {
                this.generateAutoTitle();
              }
            }
          }
        }

        this.isLoadingRfqs = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this._rfqList = [];
        this.rfqOptions = [];
        this.isLoadingRfqs = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadLoansByCompany(): void {
    if (!this.companyId) return;
    this.isLoadingLoans = true;
    this.cdr.markForCheck();

    this.loanOperationHttpService
      .filter({ page: 1, pageSize: 200 }, this.companyId)
      .pipe(
        // Card 3278: o LOAN do prefill (Painel, Derivativos, mandato) precisa estar na lista com os
        // dados, senão o seletor padrão fica em branco. Fora dos 200 primeiros da empresa, busca
        // ele pelo id e inclui.
        switchMap(result => {
          const list = result?.data ?? [];
          const missingId =
            this.loanId && !list.some(l => sameId(l.id, this.loanId)) ? this.loanId : null;
          if (!missingId) return of(list);
          return this.loanOperationHttpService
            .find(missingId)
            .pipe(map(loan => (loan ? [loan, ...list] : list)));
        })
      )
      .subscribe({
        next: loans => {
          this._loanList = loans;
          // Seletor padrão de LOAN (card 3278): o item rico lê `data`; a busca usa o `label`.
          this.loanOptions = this._loanList.map(l => ({
            label: buildLoanOptionSearchLabel(l),
            value: l.id ?? '',
            data: l,
          }));
          // O prefill pode trazer o id com outra caixa (GUID maiúsculo): usa o da lista, que é o
          // `value` da opção, para o seletor reconhecer o selecionado.
          const listed = this._loanList.find(l => sameId(l.id, this.loanId));
          if (listed?.id) {
            if (sameId(this.derivativeListLoanId, listed.id)) this.derivativeListLoanId = listed.id;
            this.loanId = listed.id;
            this.refreshVisibleDerivativeOptions();
          }
          // Resolve contrato para loans pré-preenchidos via mandato (sem contractId inicial)
          if (this.loanPrefilled && this.loanId && !this.loanContractId) {
            const found = this._loanList.find(l => sameId(l.id, this.loanId));
            if (found) {
              this.loanContractId = found.sequentialId ?? found.contractId ?? found.id ?? '';
              if (!this.titleManuallyEdited) {
                this.generateAutoTitle();
              }
            }
          }
          // Se há um loanId pré-preenchido (via operação, mandato ou rfq já resolvido)
          if (this.loanId) {
            const preselectedLoan = this._loanList.find(l => sameId(l.id, this.loanId));
            if (preselectedLoan) {
              this.loanContractId =
                (this.loanContractId ||
                  preselectedLoan.sequentialId ||
                  preselectedLoan.contractId) ??
                '';
              this._populateInstallments(preselectedLoan);
              if (!this.titleManuallyEdited) {
                this.generateAutoTitle();
              }
            }
          }

          if (this.shouldApplyDerivativeInstallmentsAfterLoanLoad) {
            this._applyDerivativeInstallmentFilter();
          }

          // Se rfq está pré-preenchida e os rfqs ainda não carregaram, verificar depois de carregar loans
          if (this.rfqPrefilled && this.rfqId && !this.loanPrefilled && !this.loanId) {
            const linkedRfq = this._rfqList.find(r => r.id === this.rfqId);
            if (linkedRfq?.loanOperationId) {
              this.loanId = linkedRfq.loanOperationId;
              const loan = this._loanList.find(l => sameId(l.id, this.loanId));
              if (loan) {
                this.loanContractId = loan.sequentialId ?? loan.contractId ?? '';
                this._populateInstallments(loan);
                if (!this.titleManuallyEdited) {
                  this.generateAutoTitle();
                }
              }
            }
          }

          this.isLoadingLoans = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this._loanList = [];
          this.loanOptions = [];
          this.isLoadingLoans = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadPipelinesByCompany(): void {
    if (!this.companyId || !this.canListPipelines()) return;
    this.isLoadingPipelines = true;
    this.cdr.markForCheck();

    const companyId = this.companyId;

    this.pipelinesHttpService
      .pipelines()
      .pipe(
        switchMap(pipelines => {
          if (!pipelines || pipelines.length === 0) return of([]);
          const requests = pipelines.map(p =>
            this.pipelinesHttpService.pipelineCompanyDeals(p.id, companyId, {})
          );
          return forkJoin(requests);
        })
      )
      .subscribe({
        next: results => {
          const allDeals = results.flat().filter(Boolean);
          this.pipelineOptions = allDeals.map(d => ({
            label: d!.reference,
            value: d!.id,
          }));
          this.isLoadingPipelines = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.pipelineOptions = [];
          this.isLoadingPipelines = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadDerivativesByCompany(): void {
    if (!this.companyId) return;
    this.isLoadingDerivatives = true;
    this.cdr.markForCheck();

    this.derivativesHttpService.getOptionsByCompany(this.companyId).subscribe({
      next: options => {
        this.derivativeOptions = options;
        this.refreshVisibleDerivativeOptions();
        this.isLoadingDerivatives = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.derivativeOptions = [];
        this.visibleDerivativeOptions = [];
        this.isLoadingDerivatives = false;
        this.cdr.markForCheck();
      },
    });
  }

  private _populateInstallments(loan: LoanOperationResponse): void {
    const installments = loan.flowParams?.installments ?? [];
    this.installmentOptions = installments.map(i => ({
      label: `Parcela #${i.installmentNumber} — ${this.dateFormatterService.formatDateInFormat(new Date(i.dueAt), 'dd/MM/yyyy')}`,
      value: i.id,
    }));
  }

  get installmentNumbersLabel(): string {
    if (this.installmentNumbers.length === 0) return '';
    return this.installmentNumbers.map(n => `#${n}`).join(', ');
  }

  private _clearLinkSelections(): void {
    this.rfqId = '';
    this.rfqSequentialId = null;
    this.loanId = '';
    this.loanContractId = '';
    this.installmentIds = [];
    this.installmentNumbers = [];
    this.pipelineCardId = '';
    this.pipelineCardRef = '';
    this.derivativeId = '';
    this.derivativeSequentialId = '';
    this.setLinkedDerivatives([]);
    this.availableDerivatives = [];
    this.availableDerivativeOptions = [];
    this._derivativeInstallmentIds = [];
    this.shouldApplyDerivativeInstallmentsAfterLoanLoad = false;
    this.rfqOptions = [];
    this.loanOptions = [];
    this.installmentOptions = [];
    this.pipelineOptions = [];
    this.derivativeOptions = [];
    this.visibleDerivativeOptions = [];
    this.derivativeListLoanId = '';
    this.titleManuallyEdited = false;
    this.title = 'Comitê';
    this._rfqList = [];
    this._loanList = [];
  }

  private reset(): void {
    this.currentStep = 1;
    this.title = 'Comitê';
    this.description = '';
    this.descriptionManuallyEdited = false;
    this.companyId = '';
    this.companyName = '';
    this.approvalType = 'Majority60Percent';
    this.deadline = null;
    this.deadlineTime = '08:00';
    this.members = [];
    this.memberPicklistResetVersion++;
    this.prefillLocked = false;
    this.loanPrefilled = false;
    this.installmentPrefilled = false;
    this.mandatePrefilled = false;
    this.riskMandateId = null;
    this.mandateSequentialId = null;
    this.rfqPrefilled = false;
    this.derivativePrefilled = false;
    this.derivativeListMode = false;
    this.shouldApplyDerivativeInstallmentsAfterLoanLoad = false;
    this._clearLinkSelections();
  }
}
